import {
  Box,
  Button,
  Flex,
  Icon,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
} from "@chakra-ui/react";
import { useContext, useState } from "react";
import { TbMessageChatbot } from "react-icons/tb";
import { FaMicrophone } from "react-icons/fa";
import { toast } from "react-toastify";
import { fetchController } from "../../utils/FetchController/fetchController";
import { getHostDomain } from "../../utils/utils";
import { AgencyContext } from "../Settings/AgencyProvider/AgencyProvider";

const TYPES = [
  {
    key: "simple",
    label: "Chat Bot",
    description: "Text-based agent for websites, WhatsApp, and SMS.",
    icon: TbMessageChatbot,
    color: "blue",
  },
  {
    key: "phone",
    label: "Voice Agent",
    description: "Spoken conversational agent for phone calls.",
    icon: FaMicrophone,
    color: "purple",
  },
];

const CreateAssistantModal = ({ isOpen, onClose, onCreated }) => {
  const baseUrl = getHostDomain();
  const agency = useContext(AgencyContext);
  const { buttonColorScheme } = agency;

  const [step, setStep] = useState(1); // 1 = choose type, 2 = name
  const [selectedType, setSelectedType] = useState(null);
  const [name, setName] = useState("");
  const [creating, setCreating] = useState(false);

  const handleCreate = async () => {
    if (!name.trim()) {
      toast.error("Please enter an assistant name");
      return;
    }
    setCreating(true);
    try {
      const res = await fetchController(baseUrl + "/api/v1/assistant", "POST", {
        name: name.trim(),
        assistant_type: selectedType,
        description: "",
        llm_model: "gpt-3.5-turbo-1106",
      });
      if (res?.data?.id) {
        onCreated(res.data.id);
      } else {
        toast.error("Failed to create assistant");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create assistant");
    } finally {
      setCreating(false);
    }
  };

  const handleClose = () => {
    setStep(1);
    setSelectedType(null);
    setName("");
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="md" isCentered>
      <ModalOverlay />
      <ModalContent>
        {step === 1 ? (
          <>
            <ModalHeader>Choose Assistant Type</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Flex gap={4}>
                {TYPES.map((t) => (
                  <Box
                    key={t.key}
                    flex={1}
                    border="2px solid"
                    borderColor={selectedType === t.key ? `${t.color}.400` : "gray.200"}
                    borderRadius="lg"
                    p={5}
                    textAlign="center"
                    cursor="pointer"
                    bg={selectedType === t.key ? `${t.color}.50` : "white"}
                    onClick={() => setSelectedType(t.key)}
                    _hover={{ borderColor: `${t.color}.300` }}
                    transition="all 0.15s"
                  >
                    <Flex
                      justify="center"
                      align="center"
                      w={14}
                      h={14}
                      borderRadius="full"
                      bg={`${t.color}.100`}
                      mx="auto"
                      mb={3}
                    >
                      <Icon as={t.icon} boxSize={7} color={`${t.color}.500`} />
                    </Flex>
                    <Text fontWeight="bold" mb={1}>{t.label}</Text>
                    <Text fontSize="xs" color="gray.500">{t.description}</Text>
                  </Box>
                ))}
              </Flex>
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={handleClose}>Cancel</Button>
              <Button
                colorScheme={buttonColorScheme}
                isDisabled={!selectedType}
                onClick={() => setStep(2)}
                rightIcon={<span>›</span>}
              >
                Next
              </Button>
            </ModalFooter>
          </>
        ) : (
          <>
            <ModalHeader>Name Your Assistant</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Text fontSize="sm" color="gray.500" mb={3}>
                Assistant Name <span style={{ color: "red" }}>*</span>
              </Text>
              <Input
                placeholder="e.g. Sales Qualifier Bot"
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") handleCreate(); }}
                autoFocus
              />
            </ModalBody>
            <ModalFooter gap={2}>
              <Button variant="ghost" onClick={() => setStep(1)}>
                ← Back
              </Button>
              <Button
                colorScheme={buttonColorScheme}
                isLoading={creating}
                isDisabled={!name.trim()}
                onClick={handleCreate}
                rightIcon={<span>›</span>}
              >
                Create Assistant
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};

export default CreateAssistantModal;
